import {
    Box,
    Button,
    Grid,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    useToast,
    Heading,
    Card,
    CardHeader,
    CardBody,
    Divider,
    Textarea,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Spacer,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import {
    useKeyPersonnel,
    useCreateKeyPersonnel,
    useUpdateKeyPersonnel,
    useDeleteKeyPersonnel,
} from '../../services/contractor-profile.hooks';

export function KeyPersonnelTab() {
    const toast = useToast();
    const { data: personnelList, isLoading } = useKeyPersonnel();
    const createPersonnel = useCreateKeyPersonnel();
    const updatePersonnel = useUpdateKeyPersonnel();
    const deletePersonnel = useDeleteKeyPersonnel();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editingId, setEditingId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        title: '',
        role: '',
        years_experience: 0,
        bio: '',
        resume_url: '',
    });

    const handleOpenAssign = (personnel?: any) => {
        if (personnel) {
            setEditingId(personnel.id);
            setFormData({
                name: personnel.name || '',
                title: personnel.title || '',
                role: personnel.role || '',
                years_experience: personnel.years_experience || 0,
                bio: personnel.bio || '',
                resume_url: personnel.resume_url || '',
            });
        } else {
            setEditingId(null);
            setFormData({
                name: '',
                title: '',
                role: '',
                years_experience: 0,
                bio: '',
                resume_url: '',
            });
        }
        onOpen();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value,
        }));
    };

    const handleSave = () => {
        if (editingId) {
            updatePersonnel.mutate(
                { id: editingId, data: formData },
                {
                    onSuccess: () => {
                        toast({ title: 'Personnel updated', status: 'success', duration: 2000 });
                        onClose();
                    },
                }
            );
        } else {
            createPersonnel.mutate(formData, {
                onSuccess: () => {
                    toast({ title: 'Personnel added', status: 'success', duration: 2000 });
                    onClose();
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        if (confirm('Are you sure you want to delete this key personnel?')) {
            deletePersonnel.mutate(id, {
                onSuccess: () => {
                    toast({ title: 'Personnel deleted', status: 'info', duration: 2000 });
                },
            });
        }
    };

    if (isLoading) return <Box>Loading...</Box>;

    return (
        <Card>
            <CardHeader>
                <HStack>
                    <Heading size="md">Key Personnel</Heading>
                    <Spacer />
                    <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" onClick={() => handleOpenAssign()}>
                        Add Personnel
                    </Button>
                </HStack>
            </CardHeader>
            <Divider color="gray.200" />
            <CardBody>
                {personnelList?.length === 0 ? (
                    <Text color="gray.500">No key personnel added yet. Click "Add Personnel" to build your team roster.</Text>
                ) : (
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th>Name</Th>
                                <Th>Title</Th>
                                <Th>Role</Th>
                                <Th isNumeric>Years Exp.</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {personnelList?.map((p: any) => (
                                <Tr key={p.id}>
                                    <Td fontWeight="medium">{p.name}</Td>
                                    <Td>{p.title}</Td>
                                    <Td>{p.role}</Td>
                                    <Td isNumeric>{p.years_experience}</Td>
                                    <Td>
                                        <HStack spacing={2}>
                                            <IconButton
                                                aria-label="Edit"
                                                icon={<MdEdit />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => handleOpenAssign(p)}
                                            />
                                            <IconButton
                                                aria-label="Delete"
                                                icon={<MdDelete />}
                                                size="xs"
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => handleDelete(p.id)}
                                            />
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </CardBody>

            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingId ? 'Edit Key Personnel' : 'Add Key Personnel'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} w="full">
                                <FormControl isRequired>
                                    <FormLabel>Full Name</FormLabel>
                                    <Input name="name" value={formData.name} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Title</FormLabel>
                                    <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g. VP of Operations" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Role in Company/Projects</FormLabel>
                                    <Input name="role" value={formData.role} onChange={handleChange} placeholder="e.g. Project Executive" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Years Experience</FormLabel>
                                    <Input name="years_experience" type="number" value={formData.years_experience} onChange={handleChange} />
                                </FormControl>
                                <FormControl gridColumn="1 / -1">
                                    <FormLabel>Short Bio</FormLabel>
                                    <Textarea name="bio" value={formData.bio} onChange={handleChange} rows={4} />
                                </FormControl>
                                <FormControl gridColumn="1 / -1">
                                    <FormLabel>Resume URL / Link</FormLabel>
                                    <Input name="resume_url" value={formData.resume_url} onChange={handleChange} placeholder="https://..." />
                                </FormControl>
                            </Grid>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleSave}
                            isLoading={createPersonnel.isPending || updatePersonnel.isPending}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Card>
    );
}
